import Vue from "vue";
import VueRouter from "vue-router";
import Layout from "@/layout/index2.vue";
import AmplifyStore from "@/store";
import * as AmplifyModules from "aws-amplify";
import NotFound from "../views/NotFound.vue";
import { routeFor } from "./guard";
import { captureReferral } from "@/utils/referral";
import { stripTicketRedirect } from "@/utils/shopifyConnect";
import { SETTINGS_SECTIONS, ADMIN_TOOLS, legacyAdminRedirects } from "./sections";

const logger = new AmplifyModules.Logger("router");

async function getUser() {
  return AmplifyModules.Auth.currentAuthenticatedUser()
    .then((data) => {
      if (data && data.signInUserSession) {
        AmplifyStore.commit("SET_USER", data);
        return data;
      }
    })
    .catch((e) => {
      logger.info(e);
      AmplifyStore.commit("SET_USER", null);
      return null;
    });
}

const routes = [
  {
    // OPT-336: no requiresAuth, like /ebayconnect: the page handles signed-out itself so the
    // ticket is not lost to the guard's bare /login redirect.
    path: "/connect/shopify",
    component: () => import("@/views/connect/ShopifyConnect"),
    hidden: true,
  },
  {
    path: "/ebayconnect",
    component: () => import("@/views/ebayconnect"),
    hidden: true,
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/login"),
    hidden: true,
  },
  {
    // Public on purpose (OPT-246): a creator's link, opened by someone who is not signed in.
    path: "/r/:code",
    name: "ReferralRedirect",
    component: () => import("@/views/ReferralRedirect.vue"),
    hidden: true,
  },
  {
    path: "/account-not-ready",
    name: "AccountNotReady",
    component: () => import("@/views/AccountNotReady.vue"),
    hidden: true,
    meta: { title: "Account not ready", requiresAuth: true, requiresAdmin: false, allowWithoutGroup: true },
  },
  {
    path: "/welcome",
    component: Layout,
    hidden: true,
    children: [
      {
        path: "/welcome",
        name: "Welcome",
        component: () => import("@/views/welcome"),
        meta: {
          title: "Welcome to Sellary",
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    ],
  },
  {
    // The landing page (2026-10-23 dashboard spec); Automation keeps /automation.
    path: "/",
    component: Layout,
    children: [
      {
        path: "/",
        name: "Dashboard",
        component: () => import("@/views/dashboard/Dashboard"),
        meta: {
          title: "Home",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-view-dashboard-outline",
          color: "teal",
        },
      },
    ],
  },
  {
    path: "/automation",
    component: Layout,
    children: [
      {
        path: "/automation",
        name: "Automation",
        component: () => import("@/views/automation"),
        meta: {
          title: "Automation",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-robot-outline",
          color: "green",
          rightDrawerForm: "AutomationForm",
        },
      },
    ],
  },
  {
    path: "/listings",
    component: Layout,
    children: [
      {
        path: "/listings",
        name: "Listings",
        component: () => import("@/views/listings"),
        meta: {
          title: "Listings",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-file-document-outline",
          color: "blue",
          rightDrawerForm: "ListingForm",
          ownsTop: true,
        },
      },
    ],
  },
  {
    // OPT-372: My Team. /team/dave is a static child so the Pro gate (meta.feature) covers Dave alone.
    path: "/team",
    component: Layout,
    children: [
      {
        path: "/team",
        name: "MyTeam",
        component: () => import("@/views/team/TeamList"),
        meta: {
          title: "My Team",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-account-group-outline",
          color: "teal",
        },
      },
      {
        path: "/team/dave/edit",
        name: "TeamDaveEdit",
        component: () => import("@/views/team/dave/EditDave"),
        meta: { feature: "automation", title: "Edit Dave", requiresAuth: true, requiresAdmin: false },
      },
      {
        path: "/team/dave",
        name: "TeamDave",
        component: () => import("@/views/team/AgentPage"),
        props: { agentId: "dave" },
        // OPT-386: Settings is its own page now; old ?tab=settings links land there.
        beforeEnter: (to, from, next) => (to.query.tab === "settings" ? next({ name: "TeamDaveEdit" }) : next()),
        meta: { feature: "automation", title: "Deal Desk Dave", requiresAuth: true, requiresAdmin: false },
      },
      {
        // OPT-415: Sally's settings are their own page, like Dave's.
        path: "/team/sally/edit",
        name: "TeamSallyEdit",
        component: () => import("@/views/team/sally/EditSally"),
        meta: { feature: "automation", title: "Edit Sally", requiresAuth: true, requiresAdmin: false },
      },
      {
        // OPT-410: Sally has a page of her own, gated the same way.
        path: "/team/sally",
        name: "TeamSally",
        component: () => import("@/views/team/sally/SallyPage"),
        beforeEnter: (to, from, next) => (to.query.tab === "settings" ? next({ name: "TeamSallyEdit" }) : next()),
        meta: { feature: "automation", title: "Social Sally", requiresAuth: true, requiresAdmin: false },
      },
      {
        // Inbox Ivy: her settings page and her page, gated on her own plan feature.
        path: "/team/ivy/edit",
        name: "TeamIvyEdit",
        component: () => import("@/views/team/ivy/EditIvy"),
        meta: { feature: "inboxAgent", title: "Edit Ivy", requiresAuth: true, requiresAdmin: false },
      },
      {
        path: "/team/ivy",
        name: "TeamIvy",
        component: () => import("@/views/team/ivy/IvyPage"),
        beforeEnter: (to, from, next) => (to.query.tab === "settings" ? next({ name: "TeamIvyEdit" }) : next()),
        meta: { feature: "inboxAgent", title: "Inbox Ivy", requiresAuth: true, requiresAdmin: false },
      },
      {
        // Dave, Sally and Ivy go through their gated records; every other agent is "Coming soon" and has no page.
        path: "/team/:agentId",
        name: "TeamAgent",
        component: () => import("@/views/team/AgentPage"),
        props: true,
        beforeEnter: (to, from, next) => {
          const own = { dave: "TeamDave", sally: "TeamSally", ivy: "TeamIvy" }[to.params.agentId];
          return own ? next({ name: own, query: to.query }) : next({ name: "MyTeam" });
        },
        meta: { title: "My Team", requiresAuth: true, requiresAdmin: false },
      },
    ],
  },
  {
    path: "/consignment",
    component: Layout,
    children: [
      // The sidebar renders ONE item per top-level route using children[0].meta,
      // so the hub must come first -- it is what "Consignment" in the nav points
      // at. The screens below are reachable from the hub's cards and by URL, but
      // deliberately do not get their own nav entries.
      {
        path: "/consignment",
        component: () => import("@/views/consignment/index"),
        meta: {
          title: "Consignment",
          requiresAuth: true,
          requiresAdmin: true,
          icon: "mdi-basket-outline",
          color: "purple",
        },
      },
      {
        path: "/consignment/dropoff",
        component: () => import("@/views/consignment/dropoff"),
        meta: {
          title: "Consigner Dropoff",
          requiresAuth: true,
          requiresAdmin: true,
          icon: "mdi-basket-plus",
          color: "purple",
        },
      },
      {
        path: "/consignment/contract",
        component: () => import("@/views/consignment/contract"),
        meta: {
          title: "Consigner Contract",
          requiresAuth: true,
          requiresAdmin: true,
          icon: "mdi-basket-document",
          color: "black",
        },
      },
      {
        path: "/consignment/batch",
        component: () => import("@/views/consignment/batch"),
        meta: {
          title: "Batch Consign",
          requiresAuth: true,
          requiresAdmin: true,
          icon: "mdi-hanger",
          color: "purple",
        },
      },
    ],
  },
  // The kiosk URL is in the wild -- bookmarks, and the address handed to staff.
  // Keep it working rather than 404ing someone mid-shift.
  {
    path: "/kiosk/contract",
    redirect: "/consignment/contract",
    hidden: true,
  },

  {
    path: "/orders",
    component: Layout,
    children: [
      {
        path: "/orders",
        name: "OrderQueue",
        component: () => import("@/views/oms/OrderQueue"),
        meta: {
          feature: "orders",
          title: "Order Queue",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-clipboard-list-outline",
          color: "indigo",
          ownsTop: true,
        },
      },
    ],
  },

  {
    path: "/profit",
    component: Layout,
    // Hidden from the menu for now (operator, 2026-10-27); /profit itself still works.
    hidden: true,
    children: [
      {
        path: "/profit",
        name: "Profit",
        component: () => import("@/views/profit/Profit"),
        meta: {
          feature: "profit",
          title: "Profit",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-currency-usd",
          color: "green",
        },
      },
    ],
  },

  // Cancellations is a tab of the Order Queue (OPT-72). The old URL is
  // bookmarked and linked from the queue's banner, so it redirects rather than
  // 404s. `hidden` keeps it out of the nav.
  {
    path: "/orders/cancellations",
    redirect: { path: "/orders", query: { tab: "cancellations" } },
    hidden: true,
  },

  {
    path: "/messages",
    component: Layout,
    children: [
      {
        path: "/messages",
        name: "Messages",
        component: () => import("@/views/oms/Messages"),
        meta: {
          feature: "inbox",
          title: "Messages",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-message-text-outline",
          color: "teal",
          badge: "messagesBadge",
        },
      },
    ],
  },

  {
    path: "/inventory",
    component: Layout,
    children: [
      {
        path: "/inventory",
        name: "Inventory",
        component: () => import("@/views/inventory-optimized"),
        meta: {
          title: "Inventory",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-hanger",
          color: "orange",
          rightDrawerForm: "InventoryForm",
          ownsTop: true,
        },
      },
      {
        path: "category",
        component: () => import("@/views/inventoryCategory"),
        meta: {
          title: "Inventory Category",
          requiresAuth: true,
          requiresAdmin: true,
          icon: "mdi-file-document",
          color: "orange",
        },
      },
      {
        path: "export",
        component: () => import("@/views/InventoryExportPage"),
        meta: {
          title: "Export Inventory",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-database-export",
          color: "orange",
        },
      },
      {
        path: "enhanced",
        component: () => import("@/views/enhanced-inventory-test"),
        meta: {
          title: "Enhanced Inventory Form",
          requiresAuth: true,
          requiresAdmin: true,
          icon: "mdi-flask",
          color: "purple",
        },
      },
      {
        path: "checklist",
        component: () => import("@/views/InventoryChecklist"),
        meta: {
          title: "Inventory Checklist",
          requiresAuth: true,
          requiresAdmin: false,
          icon: "mdi-clipboard-check",
          color: "teal",
        },
      },
    ],
  },
  {
    path: "/settings/delete-account",
    component: Layout,
    hidden: true,
    children: [
      {
        path: "/settings/delete-account",
        name: "DeleteAccount",
        component: () => import("@/views/settings/DeleteAccount"),
        meta: { title: "Delete my account", requiresAuth: true, requiresAdmin: false, allowWithoutGroup: true },
      },
    ],
  },
  // OPT-325: the Referrals page moved into Billing; old shared links still land.
  { path: "/settings/referrals", redirect: "/settings/billing", hidden: true },
  // Settings -> Sales and Settings -> Consignment merged into Cost of goods.
  { path: "/settings/sales", redirect: "/settings/cost-of-goods", hidden: true },
  { path: "/settings/consignment", redirect: "/settings/cost-of-goods", hidden: true },
  {
    path: "/settings",
    component: Layout,
    meta: { title: "Settings", requiresAuth: true, requiresAdmin: false, flushTop: true, icon: "mdi-cog-outline", color: "grey" },
    children: [
      {
        // The redirect sits on the empty-path child: children register first, so this
        // record is the one /settings matches.
        path: "",
        redirect: "/settings/accounts",
        component: () => import("@/layout/SectionShell.vue"),
        props: { heading: "Settings", sections: SETTINGS_SECTIONS },
        children: SETTINGS_SECTIONS,
      },
    ],
  },
  {
    path: "/admin",
    component: Layout,
    meta: { title: "Admin", requiresAuth: true, requiresAdmin: true, flushTop: true, icon: "mdi-shield-account-outline", color: "grey" },
    children: [
      {
        path: "",
        redirect: "/admin/brands",
        component: () => import("@/layout/SectionShell.vue"),
        props: { heading: "Admin", sections: ADMIN_TOOLS },
        children: ADMIN_TOOLS,
      },
    ],
  },
  ...legacyAdminRedirects(ADMIN_TOOLS),
  // OPT-210: /about no longer shows Rock It Resell's consignor terms.
  { path: "/about", redirect: "/", hidden: true },
  {
    // Public on purpose: consignors are not Sellary users and must be able to read what they sign.
    path: "/rir/consigner-terms",
    component: () => import("@/views/rir/ConsignerTerms"),
    hidden: true,
  },
  {
    path: "*",
    name: "Not Found",
    component: NotFound,
    hidden: true,
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

// OPT-246: any URL with ?ref=<code> remembers the code (a future site hand-off); nothing else.
router.beforeEach((to, from, next) => {
  if (to.query && typeof to.query.ref === "string") captureReferral(to.query.ref);
  // OPT-336: a Shopify connect ticket leaves the address bar before anything (the route logger,
  // history, the page) can see it; the page reads it back from sessionStorage.
  const strip = stripTicketRedirect(to);
  if (strip) return next(strip);
  next();
});

router.beforeResolve(async (to, from, next) => {
  logger.info("from-to:", from, to);

  let page = null;

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const user = await getUser();
    page = routeFor(to, {
      signedIn: !!user,
      isAdmin: AmplifyStore.getters.isAdmin,
      hasAppAccess: AmplifyStore.getters.hasAppAccess,
    });
    if (page) logger.info("Redirect to", page);
  }

  AmplifyStore.state.nav = to.meta;

  return page == null ? next() : next({ path: page });
});

Vue.use(AmplifyModules);
Vue.use(VueRouter);
export default router;
